import { useForm } from "react-hook-form";
import AppDataWrapper from "../../../../components/containers/AppDataWrapper";
import { InputField } from "../../../../components/inputs/InputField";
import { inputCurpRule, inputRequiredRule } from "../../../../helpers/form.helper";
import { AmbientestLogbookRecord, PaginatorProps, SelectType, SortParam, TableBaseHeaderProps, UserData, ValidateMatchParam } from "../../../../types";
import { AMBIENTEST_TEXT } from "../../../../config/text/ambientestText.config";
import ActionButtonsGroup from "../../../../components/containers/ActionButtonsGroup";
import { useContext, useEffect, useMemo, useState } from "react";
import { AppContext, DashboardContext } from "../../../../context";
import useDataClient from "../../../../hooks/useDataClient";
import { logbookAdapter } from "../../../../adapter/logbook.adapter";
import matchHelper from "../../../../helpers/match.helper";
import TableBase from "../../../../components/tables/TableBase";
import EmptyBox from "../../../../components/containers/EmptyBox";
import { Button, Typography } from "@mui/material";
import { rolesAdapter } from "../../../../adapter";
import { PAGINATOR_OPTIONS } from "../../../../config";
import businessHelper from "../../../../helpers/business.helper";
import InputBaseMatchSearch from "../../../../components/inputs/InputBaseMatchSearch";

const logbookSearchTypes: SelectType[] = [
    { label: 'CURP', value: 'curp' },
    { label: 'Usuario', value: 'userName' }
]

type AvaiblesSort = 'id' | 'curp' | 'userName' | 'envType' | 'status' | 'date'

interface LogbookRequest {
    searchType: 'curp' | 'userName',
    curp?: string,
    username?: string
}

export default function SarenvLogbook() {
    const appContext = useContext(AppContext)
    const context = useContext(DashboardContext)
    const listUsersClient = useDataClient<UserData[]>({ appContext: appContext })
    const recordsListClient = useDataClient<AmbientestLogbookRecord[]>({ appContext })
    const { control, formState, handleSubmit, watch, setValue } = useForm()
    const searchType = watch('searchType')
    const [request, setRequest] = useState<LogbookRequest>()
    const [currentSort, setCurrentSort] = useState<SortParam<AvaiblesSort>>()
    const [search, setSearch] = useState('')
    const [matchParams, setMatchParams] = useState<ValidateMatchParam>(matchHelper.matchParamsDefault)
    const [withPaginator, setWithPaginator] = useState<PaginatorProps>({ page: 0, perPage: PAGINATOR_OPTIONS[0] })
    const userList = useMemo<SelectType[]>(() => {
        return (listUsersClient.status.data || [{ id: 0, name: context?.appData.user || '', rol: context?.appData.rol }])
            .sort((a, b) => a.name > b.name ? 1 : -1).map(u => ({ label: u.name, value: u.name }))
    }, [listUsersClient.status])

    const listData = useMemo<AmbientestLogbookRecord[]>(() => {
        let finalList = recordsListClient.status.data || []
        if (search?.length >= 3) {
            finalList = finalList.filter(item => matchHelper.filterSearchAmbientestLogbook(item, search, matchParams))
        }
        if (currentSort) {
            finalList = [...finalList].sort((a, b) => {
                let orderFactor = currentSort.orderAsc ? 1 : -1
                switch (currentSort.sortType) {
                    case 'id':
                        return (a.id > b.id ? 1 : -1) * orderFactor
                    case 'curp':
                        return (a.curp > b.curp ? 1 : -1) * orderFactor
                    case 'date':
                        return (a.date > b.date ? 1 : -1) * orderFactor
                    case 'userName':
                        return (a.userName > b.userName ? 1 : -1) * orderFactor
                    case 'envType':
                        return (a.envType > b.envType ? 1 : -1) * orderFactor
                    case 'status':
                        return (a.id > b.id ? 1 : -1) * orderFactor
                    default:
                        return 0;
                }
            })
        }
        return finalList
    }, [recordsListClient, search, matchParams, currentSort])

    const pageData = useMemo<AmbientestLogbookRecord[]>(() => businessHelper.paginateData(listData, withPaginator), [listData, withPaginator])

    const headers = useMemo<TableBaseHeaderProps<AmbientestLogbookRecord>[]>(() => [
        {
            label: AMBIENTEST_TEXT.LOGBOOK.TABLE.ID,
            header: {
                action: () => setCurrentSort({ sortType: 'id', orderAsc: !currentSort?.orderAsc }),
                active: currentSort?.sortType === 'id'
            },
            information: ({ data }) => data.id
        },
        {
            label: AMBIENTEST_TEXT.LOGBOOK.TABLE.CURP,
            header: {
                action: () => setCurrentSort({ sortType: 'curp', orderAsc: !currentSort?.orderAsc }),
                active: currentSort?.sortType === 'curp'
            },
            information: ({ data }) => request?.curp === data.curp ? data.curp : <Button
                onClick={() => setRequest({ searchType: 'curp', curp: data.curp })}
            >
                {data.curp}
            </Button>
        },
        {
            label: AMBIENTEST_TEXT.LOGBOOK.TABLE.TYPE,
            header: {
                action: () => setCurrentSort({ sortType: 'envType', orderAsc: !currentSort?.orderAsc }),
                active: currentSort?.sortType === 'envType'
            },
            information: ({ data }) => data.envType.value ? <a href={data.envType.value.toString()} target="_blank">{data.envType.label}</a> : data.envType.label
        },
        {
            label: AMBIENTEST_TEXT.LOGBOOK.TABLE.USERNAME,
            header: {
                action: () => setCurrentSort({ sortType: 'userName', orderAsc: !currentSort?.orderAsc }),
                active: currentSort?.sortType === 'userName'
            },
            information: ({ data }) => request?.username === data.userName || userList.length === 1 ? data.userName : <Button
                onClick={() => setRequest({ searchType: 'userName', username: data.userName })}
            >
                {data.userName}
            </Button>
        },
        {
            label: AMBIENTEST_TEXT.LOGBOOK.TABLE.DATE,
            header: {
                action: () => setCurrentSort({ sortType: 'date', orderAsc: !currentSort?.orderAsc }),
                active: currentSort?.sortType === 'date'
            },
            information: ({ data }) => data.date
        },
        {
            label: AMBIENTEST_TEXT.LOGBOOK.TABLE.STATUS,
            header: {
                action: () => setCurrentSort({ sortType: 'status', orderAsc: !currentSort?.orderAsc }),
                active: currentSort?.sortType === 'status'
            },
            information: ({ data }) => <Typography color={data.status === 'Exitosa' ? 'green' : 'error'} variant='caption'>
                {data.status}
            </Typography>
        }
    ], [currentSort, request])


    function onSubmit(data: any) {
        setRequest({
            searchType: data.searchType,
            curp: data.searchType === 'curp' ? data.curp?.toUpperCase() : undefined,
            username: data.searchType === 'curp' ? undefined : data.username
        })
    }

    useEffect(() => {
        recordsListClient.clean()
        if (request) {
            setValue('curp', request.curp || '')
            setValue('username', request.username || '')
            setValue('searchType', request.searchType)
            recordsListClient.executeRequest(logbookAdapter.getAmbientestLogbook(request.curp, request.username))
        }
    }, [request])

    useEffect(() => {
        if ((context?.appData.credentials.length || 0) > 0) {
            listUsersClient.executeRequest(rolesAdapter.getUsers())
        }
    }, [])

    return (<AppDataWrapper
        paginator={request ? {
            total: pageData.length,
            setWithPaginator: setWithPaginator,
            withPaginator: withPaginator
        } : undefined}>
        <form className="form-contain" autoComplete='off' onSubmit={handleSubmit(onSubmit)}>
            <div className='form-contain-row'>
                <div>
                    <InputField
                        controller={{
                            name: "searchType",
                            control: control,
                            defaultValue: logbookSearchTypes[0].value,
                            rules: { ...inputRequiredRule }
                        }}
                        errors={formState.errors}
                        label={AMBIENTEST_TEXT.LOGBOOK.LABELS.DATA_TYPE}
                        options={logbookSearchTypes}
                        disabled={!!request}
                    />
                </div>
                <div>
                    {searchType === logbookSearchTypes[0].value ? <InputField
                        key={'curp'}
                        controller={{
                            name: "curp",
                            control: control,
                            defaultValue: '',
                            rules: { ...inputRequiredRule, ...inputCurpRule }
                        }}
                        inputProps={{
                            maxLength: 18,
                            style: { textTransform: "uppercase" }
                        }}
                        errors={formState.errors}
                        label={AMBIENTEST_TEXT.LOGBOOK.LABELS.SEARCH_CURP}
                        disabled={!!request}
                    /> : <InputField
                        key={'username'}
                        controller={{
                            name: "username",
                            control: control,
                            defaultValue: '',
                            rules: { ...inputRequiredRule }
                        }}
                        errors={formState.errors}
                        label={AMBIENTEST_TEXT.LOGBOOK.LABELS.SEARCH_USER}
                        options={userList}
                        disabled={!!request}
                    />}
                </div>
                <div className='d-flex'>
                    <ActionButtonsGroup
                        className='m-r-auto m-l-auto'
                        items={!request ? [{
                            title: AMBIENTEST_TEXT.LOGBOOK.BUTTONS.SEARCH,
                            icon: 'search',
                            label: AMBIENTEST_TEXT.LOGBOOK.BUTTONS.SEARCH,
                            type: 'submit'
                        }] : [{
                            title: AMBIENTEST_TEXT.LOGBOOK.BUTTONS.CLEAN,
                            icon: 'eraser',
                            label: AMBIENTEST_TEXT.LOGBOOK.BUTTONS.CLEAN,
                            action: () => {
                                setRequest(undefined)
                            }
                        }]}
                    />
                </div>
            </div>
            {request ? <>
                <div className='data-grid'>
                    <div className='data-grid-item m-l-auto d-flex'>
                        <InputBaseMatchSearch
                            updateParams={(search, match) => {
                                setMatchParams(match)
                                setSearch(search)
                            }}
                        />
                    </div>
                </div>
                <TableBase<AmbientestLogbookRecord> headers={headers} data={pageData} />
            </> : <EmptyBox label='' styles={{ marginTop: '5rem' }} />}
        </form>
    </AppDataWrapper>
    )
}